// src/utils/validation.js
// Zentrale Validierungsfunktionen für Input-Sicherheit

/**
 * Entfernt potenziell gefährliche HTML/Script-Tags
 * @param {string} input - Der zu bereinigende Text
 * @returns {string} Bereinigter Text
 */
export function sanitizeText(input) {
  if (typeof input !== 'string') return '';

  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
}

/**
 * Validiert eine Zahl innerhalb eines Bereichs
 * @param {any} input - Der zu prüfende Wert
 * @param {number} min - Minimaler Wert
 * @param {number} max - Maximaler Wert
 * @returns {{ valid: boolean, value: number|null, error: string|null }}
 */
export function validateNumber(input, min = -Infinity, max = Infinity) {
  const num = Number(input);

  if (isNaN(num)) {
    return { valid: false, value: null, error: 'Keine gültige Zahl' };
  }

  if (num < min) {
    return { valid: false, value: null, error: `Wert muss mindestens ${min} sein` };
  }

  if (num > max) {
    return { valid: false, value: null, error: `Wert darf maximal ${max} sein` };
  }

  return { valid: true, value: num, error: null };
}

/**
 * Validiert eine E-Mail-Adresse
 * @param {string} input - Die zu prüfende E-Mail
 * @returns {{ valid: boolean, error: string|null }}
 */
export function validateEmail(input) {
  if (typeof input !== 'string' || input.trim() === '') {
    return { valid: false, error: 'E-Mail darf nicht leer sein' };
  }

  // Einfaches E-Mail-Pattern
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  if (!emailPattern.test(input.trim())) {
    return { valid: false, error: 'Ungültiges E-Mail-Format' };
  }

  return { valid: true, error: null };
}

/**
 * Validiert Passwort-Stärke
 * @param {string} password - Das zu prüfende Passwort
 * @param {number} minLength - Minimale Länge (default: 8)
 * @returns {{ valid: boolean, error: string|null, strength: 'weak'|'medium'|'strong' }}
 */
export function validatePassword(password, minLength = 8) {
  if (typeof password !== 'string') {
    return { valid: false, error: 'Passwort muss ein Text sein', strength: 'weak' };
  }

  if (password.length < minLength) {
    return {
      valid: false,
      error: `Passwort muss mindestens ${minLength} Zeichen haben`,
      strength: 'weak',
    };
  }

  let strength = 'weak';
  const hasLowercase = /[a-z]/.test(password);
  const hasUppercase = /[A-Z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);

  const score = [hasLowercase, hasUppercase, hasNumbers, hasSpecial].filter(Boolean).length;

  if (score >= 4) strength = 'strong';
  else if (score >= 2) strength = 'medium';

  return { valid: true, error: null, strength };
}

/**
 * Validiert einen Benutzernamen
 * @param {string} name - Der zu prüfende Name
 * @param {number} minLength - Minimale Länge (default: 2)
 * @param {number} maxLength - Maximale Länge (default: 50)
 * @returns {{ valid: boolean, error: string|null }}
 */
export function validateName(name, minLength = 2, maxLength = 50) {
  if (typeof name !== 'string') {
    return { valid: false, error: 'Name muss ein Text sein' };
  }

  const trimmed = name.trim();

  if (trimmed.length < minLength) {
    return { valid: false, error: `Name muss mindestens ${minLength} Zeichen haben` };
  }

  if (trimmed.length > maxLength) {
    return { valid: false, error: `Name darf maximal ${maxLength} Zeichen haben` };
  }

  // Nur Buchstaben, Zahlen, Leerzeichen, Bindestriche erlauben
  if (!/^[\p{L}\p{N}\s\-']+$/u.test(trimmed)) {
    return { valid: false, error: 'Name enthält ungültige Zeichen' };
  }

  return { valid: true, error: null };
}
